import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, CalendarPlus, Share2, Ticket } from "lucide-react";
import { toast } from "sonner";

const CONFETTI_COLORS = ["#AAEE00", "#7B2FFF", "#FF4D6D", "#00D4FF", "#F5F5F7"];

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 90 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 2.2,
    duration: 2.6 + Math.random() * 2.4,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    rotate: Math.random() * 360,
    scale: 0.6 + Math.random() * 0.9,
  })), []);
  return (
    <div aria-hidden data-testid="confetti-layer">
      {pieces.map((p, i) => (
        <span key={i} className="confetti-piece" style={{
          left: `${p.left}vw`, background: p.color,
          animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`,
          transform: `rotate(${p.rotate}deg) scale(${p.scale})`,
          borderRadius: i % 3 === 0 ? "999px" : "2px",
        }} />
      ))}
    </div>
  );
}

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;
    const poll = async () => {
      if (stopped || attempts > 20) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus(data);
        if (data.payment_status === "paid") return;
      } catch {}
      setTimeout(() => setAttempts((n) => n + 1), 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, attempts]);

  const paid = status.payment_status === "paid";
  const failed = status.payment_status === "failed" || status.payment_status === "expired";

  useEffect(() => {
    if (paid && status.event_id && !event) {
      api.get(`/events/${status.event_id}`).then(({ data }) => setEvent(data)).catch(() => {});
    }
  }, [paid, status.event_id, event]);

  const addToCalendar = () => {
    const title = encodeURIComponent(status.event_title || event?.title || "Eventom Event");
    let dates = "";
    if (event?.date) {
      const d = event.date.split("T")[0].replace(/-/g, "");
      if (event.time) {
        const start = `${d}T${event.time.replace(":", "")}00`;
        const endD = (event.end_date || event.date).split("T")[0].replace(/-/g, "");
        const end = event.end_time ? `${endD}T${event.end_time.replace(":", "")}00` : `${endD}T235900`;
        dates = `&dates=${start}/${end}`;
      } else {
        // All-day: Google Calendar end date is EXCLUSIVE → end = day after last day
        const last = new Date((event.end_date || event.date).split("T")[0]);
        last.setDate(last.getDate() + 1);
        const endD = last.toISOString().split("T")[0].replace(/-/g, "");
        dates = `&dates=${d}/${endD}`;
      }
    }
    const details = encodeURIComponent(`Booked on Eventom ${window.location.origin}/events/${status.event_id || ""}`);
    const loc = encodeURIComponent(event?.location || event?.city || "");
    window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}${dates}&details=${details}&location=${loc}`, "_blank", "noopener");
  };

  const inviteFriends = async () => {
    const url = `${window.location.origin}/events/${status.event_id || ""}`;
    const text = `I'm going to ${status.event_title || "an event"} Grab your ticket on Eventom: ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: status.event_title, text, url }); return; } catch {}
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    try { await navigator.clipboard.writeText(url); toast.success("Link copied too!"); } catch {}
  };

  return (
    <Layout>
      {paid && <Confetti />}
      <div className="max-w-2xl mx-auto px-4 py-16 relative z-10">
        <div className={`glass rounded-3xl p-8 ${paid ? "!border-lime/50 shadow-brutal-lime" : failed ? "!border-magenta/50" : ""}`}>
          {paid ? <CheckCircle2 className="w-14 h-14 text-lime" style={{ filter: "drop-shadow(0 0 14px rgba(170,238,0,0.7))" }} /> : failed ? <XCircle className="w-12 h-12 text-magenta" /> : <Loader2 className="w-12 h-12 animate-spin text-white" />}
          <div className="font-mono text-[10px] tracking-widest uppercase mt-4 text-muted">{paid ? "Ticket confirmed" : failed ? "Payment failed" : "Confirming..."}</div>
          <h1 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] mt-2 grad-text" data-testid="payment-status-heading">
            {paid ? "You're in!" : failed ? "Something went wrong." : "Almost there..."}
          </h1>
          <p className="mt-3 text-text/80">
            {paid ? `Ticket reserved for ${status.event_title || "the event"}. Amount: ₹${status.amount}` : failed ? "Payment didn't go through. Please try again." : "Confirming your payment..."}
          </p>
          {paid && (
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={addToCalendar} data-testid="add-to-calendar-btn" className="btn-orange"><CalendarPlus className="w-4 h-4" /> Add to Calendar</button>
              <button onClick={inviteFriends} data-testid="invite-friends-btn" className="btn-pink"><Share2 className="w-4 h-4" /> Invite Friends</button>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/profile" data-testid="payment-goto-profile" className="btn-primary"><Ticket className="w-4 h-4" /> View My Tickets</Link>
            <Link to="/" className="btn-ghost">Back to Events</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
